import React, { useState, useMemo } from 'react';
import {
  useAutomationRules,
  useAutomationRule,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  useDeleteAutomationRule,
  useToggleAutomationRule,
  useReorderAutomationRules,
  useSeedDefaultAutomationRules,
} from '../hooks/useAutomationRules';
import { useCategories } from '../hooks/useCategories';
import { useArticleCollections } from '../hooks/useArticleCollections';
import { TreeSelect } from './tree-select/TreeSelect';
import { activeCategoryOptions, categoryToOption, collectionToOption, type TreeOption } from './tree-select/treeOptions';
import { useAssignableUsers } from '../hooks/useUsers';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { showToast } from '../lib/toast';
import {
  Plus,
  ArrowLeft,
  Pencil,
  Trash2,
  Zap,
  X,
  ChevronUp,
  ChevronDown,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AutomationRuleType,
  AutomationRuleTypeLabels,
  AutomationEvent,
  AutomationEventLabels,
  AutomationActionType,
  AutomationActionTypeLabels,
  AutomationActionTypeGroup,
  AutomationActionTypeDescriptions,
  ConditionField,
  ConditionFieldLabels,
  ConditionFieldGroup,
  ConditionFieldDescriptions,
  ConditionOperator,
  ConditionOperatorLabels,
  ConditionOperatorHints,
  RepeatBehavior,
  RepeatBehaviorLabels,
  RepeatBehaviorDescriptions,
  AutomationRuleTypeDescriptions,
  AutomationEventDescriptions,
  getConditionFieldValueType,
  getValidOperatorsForField,
  doesActionRequireConfig,
  getValidConditionFieldsForEvent,
  getValidActionTypesForEvent,
  getEntityContextFromConditions,
  getValidConditionFieldsForEntityContext,
  getValidActionTypesForEntityContext,
  isArticleEvent,
} from '../types/automationRules';
import type {
  AutomationRuleListItem,
  ConditionGroupDto,
  ConditionDto,
  ActionDto,
} from '../types/automationRules';
import { TicketStatusLabels, PriorityLabels } from '../types/tickets';

// ArticleVisibility enum values, as the engine compares them
const ARTICLE_VISIBILITY_OPTIONS = [
  { value: '0', label: 'Public' },
  { value: '1', label: 'Internal' },
];

// Assignment actions that can be limited to a pool of agents ({ "agentIds": [...] }, optional)
const POOL_ACTION_TYPES: AutomationActionType[] = [
  AutomationActionType.AssignRoundRobin,
  AutomationActionType.AssignLoadBalanced,
];

// ── Duration helpers ──

type DurationUnit = 'minutes' | 'hours' | 'days';

function hoursToUnit(hours: number, unit: DurationUnit): number {
  switch (unit) {
    case 'minutes': return hours * 60;
    case 'days': return hours / 24;
    default: return hours;
  }
}

function unitToHours(value: number, unit: DurationUnit): number {
  switch (unit) {
    case 'minutes': return value / 60;
    case 'days': return value * 24;
    default: return value;
  }
}

function inferBestUnit(hours: number): DurationUnit {
  if (hours === 0) return 'hours';
  if (hours >= 24 && hours % 24 === 0) return 'days';
  if (hours < 1 || (hours * 60) % 1 === 0) return 'minutes';
  return 'hours';
}

// ── Editor form types ──

interface EditorCondition {
  tempId: string;
  field: ConditionField | null;
  operator: ConditionOperator | null;
  value: string;
}

interface EditorConditionGroup {
  tempId: string;
  conditions: EditorCondition[];
}

interface EditorAction {
  tempId: string;
  actionType: AutomationActionType | null;
  configuration: string;
}

interface EditorFormState {
  name: string;
  description: string;
  ruleType: AutomationRuleType;
  triggerEvent: AutomationEvent | null;
  stopProcessing: boolean;
  repeatBehavior: RepeatBehavior;
  conditionGroups: EditorConditionGroup[];
  actions: EditorAction[];
}

function createEmptyCondition(): EditorCondition {
  return { tempId: crypto.randomUUID(), field: null, operator: null, value: '' };
}

function createEmptyGroup(): EditorConditionGroup {
  return { tempId: crypto.randomUUID(), conditions: [createEmptyCondition()] };
}

function createEmptyAction(): EditorAction {
  return { tempId: crypto.randomUUID(), actionType: null, configuration: '' };
}

const INITIAL_FORM: EditorFormState = {
  name: '',
  description: '',
  ruleType: AutomationRuleType.Trigger,
  triggerEvent: null,
  stopProcessing: false,
  repeatBehavior: RepeatBehavior.RunOncePerTicket,
  conditionGroups: [],
  actions: [createEmptyAction()],
};

// ── Helpers for grouped selects ──

function groupByValue<T extends number>(
  items: Record<T, string>,
  groupMap: Record<T, string>
): Record<string, { value: T; label: string }[]> {
  const groups: Record<string, { value: T; label: string }[]> = {};
  for (const [valStr, label] of Object.entries(items) as [string, string][]) {
    const val = Number(valStr) as T;
    const group = groupMap[val] || 'Other';
    if (!groups[group]) groups[group] = [];
    groups[group].push({ value: val, label });
  }
  return groups;
}

// ── Sub-components ──

interface ConditionValueInputProps {
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
  onChange: (value: string) => void;
  organizationId: string;
}

const ConditionValueInput: React.FC<ConditionValueInputProps> = ({ field, operator, value, onChange, organizationId }) => {
  const valueType = getConditionFieldValueType(field);
  const isMultiSelect = operator === ConditionOperator.In || operator === ConditionOperator.NotIn;

  // Archived categories included: tickets keep them, so a condition can still match one
  const { data: categories } = useCategories(
    (field === ConditionField.CategoryId) ? organizationId : undefined,
    { includeArchived: true }
  );
  const { data: collections } = useArticleCollections(
    (field === ConditionField.ArticleCollectionId) ? organizationId : undefined
  );
  const { data: agents } = useAssignableUsers(
    (field === ConditionField.AssignedToId) ? organizationId : undefined
  );

  if (valueType === 'tree') {
    const isCategory = field === ConditionField.CategoryId;
    const options: TreeOption[] = isCategory
      ? (categories ?? []).map(categoryToOption)
      : (collections ?? []).map(collectionToOption);
    const noun = isCategory ? 'category' : 'collection';
    const hint = ConditionOperatorHints[operator];

    if (isMultiSelect) {
      let selectedValues: string[] = [];
      try { selectedValues = value ? JSON.parse(value) : []; } catch { selectedValues = value ? [value] : []; }
      const toggleValue = (id: string) => {
        const updated = selectedValues.includes(id) ? selectedValues.filter((v) => v !== id) : [...selectedValues, id];
        onChange(updated.length > 0 ? JSON.stringify(updated) : '');
      };
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start font-normal">
              {selectedValues.length > 0
                ? `${selectedValues.length} ${selectedValues.length === 1 ? noun : isCategory ? 'categories' : 'collections'} selected`
                : `Select ${isCategory ? 'categories' : 'collections'}`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="start">
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {options.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                  style={{ paddingLeft: `${8 + (opt.depth - 1) * 16}px` }}
                >
                  <Checkbox checked={selectedValues.includes(opt.id)} onCheckedChange={() => toggleValue(opt.id)} />
                  <span className="truncate">
                    {opt.name}
                    {opt.archived && <span className="text-muted-foreground"> (archived)</span>}
                  </span>
                </label>
              ))}
              {options.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1">No {isCategory ? 'categories' : 'collections'} yet</p>}
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    // A rule saved through the API may hold a list for "is within"; the picker edits a single value
    const single = value && !value.startsWith('[') ? value : null;
    return (
      <div className="space-y-1">
        <TreeSelect
          options={options}
          value={single}
          onChange={(id) => onChange(id ?? '')}
          placeholder={`Select ${noun}`}
          searchPlaceholder={`Search ${isCategory ? 'categories' : 'collections'}...`}
          emptyMessage={`No ${isCategory ? 'categories' : 'collections'}`}
        />
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  }

  if (valueType === 'id') {
    if (field === ConditionField.AssignedToId) {
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select agent" /></SelectTrigger>
          <SelectContent>
            {(agents || []).map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>{agent.fullName || agent.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
  }

  if (valueType === 'enum') {
    // Multi-select for In/NotIn operators
    if (isMultiSelect) {
      let selectedValues: string[] = [];
      try { selectedValues = value ? JSON.parse(value) : []; } catch { selectedValues = value ? [value] : []; }

      let options: { value: string; label: string }[] = [];
      if (field === ConditionField.Status) {
        options = Object.entries(TicketStatusLabels).map(([val, label]) => ({ value: val, label }));
      } else if (field === ConditionField.Priority) {
        options = Object.entries(PriorityLabels).map(([val, label]) => ({ value: val, label }));
      } else if (field === ConditionField.CreatedByType) {
        options = [
          { value: 'Agent', label: 'Agent' },
          { value: 'Customer', label: 'Customer' },
          { value: 'System', label: 'System' },
        ];
      } else if (field === ConditionField.ArticleVisibility) {
        options = ARTICLE_VISIBILITY_OPTIONS;
      }

      const toggleValue = (val: string) => {
        const updated = selectedValues.includes(val)
          ? selectedValues.filter((v) => v !== val)
          : [...selectedValues, val];
        onChange(JSON.stringify(updated));
      };

      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start font-normal">
              {selectedValues.length > 0
                ? `${selectedValues.length} selected`
                : 'Select values'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start">
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {options.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={selectedValues.includes(opt.value)}
                    onCheckedChange={() => toggleValue(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    if (field === ConditionField.Status) {
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select status" /></SelectTrigger>
          <SelectContent>
            {Object.entries(TicketStatusLabels).map(([val, label]) => (
              <SelectItem key={val} value={val}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (field === ConditionField.Priority) {
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select priority" /></SelectTrigger>
          <SelectContent>
            {Object.entries(PriorityLabels).map(([val, label]) => (
              <SelectItem key={val} value={val}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (field === ConditionField.ArticleVisibility) {
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select visibility" /></SelectTrigger>
          <SelectContent>
            {ARTICLE_VISIBILITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (field === ConditionField.CreatedByType) {
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Agent">Agent</SelectItem>
            <SelectItem value="Customer">Customer</SelectItem>
            <SelectItem value="System">System</SelectItem>
          </SelectContent>
        </Select>
      );
    }
  }

  if (valueType === 'boolean') {
    return (
      // Lowercased: the seeded default rules store "False", and the engine compares either way
      <Select value={value?.toLowerCase()} onValueChange={onChange}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (valueType === 'duration') {
    return <DurationValueInput value={value} onChange={onChange} />;
  }

  if (valueType === 'number') {
    return (
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter number"
      />
    );
  }

  if (valueType === 'date') {
    return (
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Select date"
      />
    );
  }

  // text fallback
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter value"
    />
  );
};

// A duration in its own component, so its unit state is a hook called on every render of it - not inside a branch
const DurationValueInput: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const storedHours = parseFloat(value) || 0;
  const [unit, setUnit] = React.useState<DurationUnit>(() => inferBestUnit(storedHours));
  const displayValue = storedHours ? hoursToUnit(storedHours, unit) : '';

  return (
    <div className="flex gap-2">
      <Input
        type="number"
        min="0"
        step="any"
        className="flex-1"
        value={displayValue}
        onChange={(e) => {
          const num = parseFloat(e.target.value);
          if (isNaN(num) || e.target.value === '') {
            onChange('');
          } else {
            onChange(unitToHours(num, unit).toString());
          }
        }}
        placeholder="Enter duration"
      />
      <Select
        value={unit}
        onValueChange={(v) => {
          const newUnit = v as DurationUnit;
          setUnit(newUnit);
          if (value) {
            const hours = parseFloat(value);
            if (!isNaN(hours)) {
              // Value stays the same in hours, display updates
              onChange(hours.toString());
            }
          }
        }}
      >
        <SelectTrigger className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="minutes">Minutes</SelectItem>
          <SelectItem value="hours">Hours</SelectItem>
          <SelectItem value="days">Days</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

interface ActionConfigInputProps {
  actionType: AutomationActionType;
  configuration: string;
  onChange: (config: string) => void;
  organizationId: string;
}

const ActionConfigInput: React.FC<ActionConfigInputProps> = ({ actionType, configuration, onChange, organizationId }) => {
  // Parse existing config
  const parsed = useMemo(() => {
    if (!configuration) return {};
    try { return JSON.parse(configuration); } catch { return {}; }
  }, [configuration]);

  // Fetched with archived ones so a rule already set to an archived category still shows it; only active ones are
  // offered, and the engine skips an archived category when the rule runs
  const { data: categories } = useCategories(
    (actionType === AutomationActionType.SetCategory) ? organizationId : undefined,
    { includeArchived: true }
  );
  const { data: agents } = useAssignableUsers(
    (actionType === AutomationActionType.AssignToAgent ||
     actionType === AutomationActionType.AddFollower ||
     actionType === AutomationActionType.NotifySpecificAgents ||
     actionType === AutomationActionType.SendEmailToSpecificAgents ||
     POOL_ACTION_TYPES.includes(actionType))
      ? organizationId : undefined
  );

  if (actionType === AutomationActionType.SetStatus) {
    const currentValue = parsed.value?.toString() || '';
    return (
      <Select value={currentValue} onValueChange={(v) => onChange(JSON.stringify({ value: Number(v) }))}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Select status" /></SelectTrigger>
        <SelectContent>
          {Object.entries(TicketStatusLabels).map(([val, label]) => (
            <SelectItem key={val} value={val}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (actionType === AutomationActionType.SetPriority) {
    const currentValue = parsed.value?.toString() || '';
    return (
      <Select value={currentValue} onValueChange={(v) => onChange(JSON.stringify({ value: Number(v) }))}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Select priority" /></SelectTrigger>
        <SelectContent>
          {Object.entries(PriorityLabels).map(([val, label]) => (
            <SelectItem key={val} value={val}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (actionType === AutomationActionType.SetCategory) {
    const currentValue: string | null = parsed.categoryId || null;
    // Active categories, plus the current one if it has since been archived (the engine skips it)
    const options = activeCategoryOptions(categories, currentValue).map((opt) =>
      opt.archived ? { ...opt, hint: 'skipped when the rule runs' } : opt
    );
    return (
      <TreeSelect
        options={options}
        value={currentValue}
        onChange={(id) => onChange(id ? JSON.stringify({ categoryId: id }) : '')}
        placeholder="Select category"
        searchPlaceholder="Search categories..."
        emptyMessage="No categories"
      />
    );
  }

  if (POOL_ACTION_TYPES.includes(actionType)) {
    const selectedIds: string[] = Array.isArray(parsed.agentIds) ? parsed.agentIds : [];
    const unavailable = selectedIds.filter((id) => !(agents || []).some((a) => a.id === id)).length;
    return (
      <div className="space-y-1">
        <AgentMultiSelect
          agents={agents || []}
          selectedIds={selectedIds}
          emptyLabel="Everyone assignable"
          // No pool means every assignable member, as before
          onChange={(ids) => onChange(ids.length > 0 ? JSON.stringify({ agentIds: ids }) : '')}
        />
        <p className="text-xs text-muted-foreground">
          {selectedIds.length > 0
            ? 'Only these agents are considered. If none of them can take tickets, nobody is assigned.'
            : 'Optional: limit the assignment to a team.'}
          {unavailable > 0 && (
            <span className="text-amber-600 dark:text-amber-400">
              {' '}{unavailable} selected {unavailable === 1 ? 'agent is' : 'agents are'} no longer assignable and will be skipped.
            </span>
          )}
        </p>
      </div>
    );
  }

  if (actionType === AutomationActionType.AssignToAgent || actionType === AutomationActionType.AddFollower) {
    const currentValue = parsed.agentId || '';
    return (
      <Select value={currentValue} onValueChange={(v) => onChange(JSON.stringify({ agentId: v }))}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={actionType === AutomationActionType.AssignToAgent ? "Select agent" : "Select follower"} />
        </SelectTrigger>
        <SelectContent>
          {(agents || []).map((agent) => (
            <SelectItem key={agent.id} value={agent.id}>
              {agent.fullName || agent.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (actionType === AutomationActionType.NotifySpecificAgents || actionType === AutomationActionType.SendEmailToSpecificAgents) {
    const selectedIds: string[] = Array.isArray(parsed.agentIds) ? parsed.agentIds : [];
    return (
      <AgentMultiSelect
        agents={agents || []}
        selectedIds={selectedIds}
        emptyLabel="Select agents"
        onChange={(ids) => onChange(JSON.stringify({ agentIds: ids }))}
      />
    );
  }

  return null;
};

interface AgentMultiSelectProps {
  agents: { id: string; fullName?: string; email: string }[];
  selectedIds: string[];
  emptyLabel: string;
  onChange: (ids: string[]) => void;
}

// Agents picked for an action: the "specific agents" notify/email actions, and assignment pools
const AgentMultiSelect: React.FC<AgentMultiSelectProps> = ({ agents, selectedIds, emptyLabel, onChange }) => {
  const toggleAgent = (agentId: string) => {
    onChange(selectedIds.includes(agentId)
      ? selectedIds.filter((id) => id !== agentId)
      : [...selectedIds, agentId]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start font-normal">
          {selectedIds.length > 0
            ? `${selectedIds.length} agent${selectedIds.length !== 1 ? 's' : ''} selected`
            : emptyLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {agents.map((agent) => (
            <label
              key={agent.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
            >
              <Checkbox
                checked={selectedIds.includes(agent.id)}
                onCheckedChange={() => toggleAgent(agent.id)}
              />
              {agent.fullName || agent.email}
            </label>
          ))}
          {agents.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-1">No agents available</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Condition Row ──

const timeBasedConditionFields: ConditionField[] = [
  ConditionField.HoursSinceCreated,
  ConditionField.HoursSinceUpdated,
  ConditionField.HoursSinceStatusChange,
  ConditionField.HoursSinceAssigned,
  ConditionField.HoursSinceLastActivity,
  ConditionField.HoursUntilDue,
  ConditionField.ReminderCountSinceLastActivity,
  ConditionField.HoursSinceArticleCreated,
  ConditionField.HoursSinceArticleUpdated,
  ConditionField.HoursSinceArticlePublished,
];

const triggerOnlyConditionFields: ConditionField[] = [
  ConditionField.CreatedByType,
  ConditionField.IsCommentInternal,
];

interface ConditionRowProps {
  condition: EditorCondition;
  onUpdate: (updates: Partial<EditorCondition>) => void;
  onRemove: () => void;
  organizationId: string;
  ruleType: AutomationRuleType;
  triggerEvent?: AutomationEvent | null;
  entityContextFields?: ConditionField[] | null;
}

const ConditionRow: React.FC<ConditionRowProps> = ({ condition, onUpdate, onRemove, organizationId, ruleType, triggerEvent, entityContextFields }) => {
  const operators = condition.field ? getValidOperatorsForField(condition.field) : [];
  const isSetOperator = condition.operator === ConditionOperator.IsSet || condition.operator === ConditionOperator.IsNotSet;

  const validFieldsForEvent = getValidConditionFieldsForEvent(triggerEvent);
  // Use event-based filtering for Trigger rules, entity-context filtering for Automation rules
  const effectiveFieldFilter = validFieldsForEvent || entityContextFields;

  const allFieldGroups = groupByValue(ConditionFieldLabels, ConditionFieldGroup);
  // Filter by rule type AND trigger event / entity context
  const conditionFieldGroups: Record<string, { value: ConditionField; label: string }[]> = {};
  for (const [group, fields] of Object.entries(allFieldGroups)) {
    let filtered = ruleType === AutomationRuleType.Trigger
      ? fields.filter((f) => !timeBasedConditionFields.includes(f.value))
      : fields.filter((f) => !triggerOnlyConditionFields.includes(f.value));
    // Apply event-based or entity-context filtering
    if (effectiveFieldFilter) {
      filtered = filtered.filter((f) => effectiveFieldFilter.includes(f.value));
    }
    if (filtered.length > 0) conditionFieldGroups[group] = filtered;
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
      {/* Field select */}
      <Select
        value={condition.field?.toString() || ''}
        onValueChange={(v) => onUpdate({ field: Number(v) as ConditionField, operator: null, value: '' })}
      >
        <SelectTrigger className="w-full sm:w-48">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(conditionFieldGroups).map(([group, fields]) => (
            <SelectGroup key={group}>
              <SelectLabel>{group}</SelectLabel>
              {fields.map((f) => {
                const desc = ConditionFieldDescriptions[f.value as ConditionField];
                return (
                  <SelectItem key={f.value} value={f.value.toString()}>
                    <div>
                      <span>{f.label}</span>
                      {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Operator select */}
      {condition.field !== null && (
        <Select
          value={condition.operator?.toString() || ''}
          onValueChange={(v) => {
            const newOp = Number(v) as ConditionOperator;
            const wasMulti = condition.operator === ConditionOperator.In || condition.operator === ConditionOperator.NotIn;
            const isMulti = newOp === ConditionOperator.In || newOp === ConditionOperator.NotIn;
            const wasSet = condition.operator === ConditionOperator.IsSet || condition.operator === ConditionOperator.IsNotSet;
            const isSet = newOp === ConditionOperator.IsSet || newOp === ConditionOperator.IsNotSet;
            // Switching between a single value and a list (a JSON array of the same values) keeps the selection
            let value: string | undefined;
            if (wasSet !== isSet) {
              value = '';
            } else if (!wasMulti && isMulti) {
              value = condition.value ? JSON.stringify([condition.value]) : '';
            } else if (wasMulti && !isMulti) {
              let list: unknown = [];
              try { list = JSON.parse(condition.value || '[]'); } catch { /* not a list: start empty */ }
              value = Array.isArray(list) && typeof list[0] === 'string' ? list[0] : '';
            }
            onUpdate({ operator: newOp, ...(value !== undefined ? { value } : {}) });
          }}
        >
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            {operators.map((op) => (
              <SelectItem key={op} value={op.toString()}>{ConditionOperatorLabels[op]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Value input */}
      {condition.field !== null && condition.operator !== null && !isSetOperator && (
        <div className="flex-1 w-full sm:w-auto">
          <ConditionValueInput
            field={condition.field}
            operator={condition.operator}
            value={condition.value}
            onChange={(v) => onUpdate({ value: v })}
            organizationId={organizationId}
          />
        </div>
      )}

      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" onClick={onRemove}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// ── Action Row ──

const automationOnlyActionTypes: AutomationActionType[] = [
  AutomationActionType.RecordReminder,
];

interface ActionRowProps {
  action: EditorAction;
  index: number;
  total: number;
  onUpdate: (updates: Partial<EditorAction>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  organizationId: string;
  ruleType: AutomationRuleType;
  triggerEvent?: AutomationEvent | null;
  entityContextActions?: AutomationActionType[] | null;
}

const ActionRow: React.FC<ActionRowProps> = ({ action, index, total, onUpdate, onRemove, onMove, organizationId, ruleType, triggerEvent, entityContextActions }) => {
  const validActionsForEvent = getValidActionTypesForEvent(triggerEvent);
  // Use event-based filtering for Trigger rules, entity-context filtering for Automation rules
  const effectiveActionFilter = validActionsForEvent || entityContextActions;

  const allGroups = groupByValue(AutomationActionTypeLabels, AutomationActionTypeGroup);
  const actionGroups: Record<string, { value: AutomationActionType; label: string }[]> = {};
  for (const [group, actions] of Object.entries(allGroups)) {
    let filtered = actions.filter((a) => {
      if (ruleType === AutomationRuleType.Trigger && automationOnlyActionTypes.includes(a.value)) return false;
      return true;
    });
    // Apply event-based or entity-context filtering
    if (effectiveActionFilter) {
      filtered = filtered.filter((a) => effectiveActionFilter.includes(a.value));
    }
    if (filtered.length > 0) actionGroups[group] = filtered;
  }
  // Assignment pools are optional, but still configured here
  const needsConfig = action.actionType !== null &&
    (doesActionRequireConfig(action.actionType) || POOL_ACTION_TYPES.includes(action.actionType));

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
      {/* Action type select */}
      <Select
        value={action.actionType?.toString() || ''}
        onValueChange={(v) => onUpdate({ actionType: Number(v) as AutomationActionType, configuration: '' })}
      >
        <SelectTrigger className="w-full sm:w-56">
          <SelectValue placeholder="Select action" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(actionGroups).map(([group, actions]) => (
            <SelectGroup key={group}>
              <SelectLabel>{group}</SelectLabel>
              {actions.map((a) => {
                const desc = AutomationActionTypeDescriptions[a.value as AutomationActionType];
                return (
                  <SelectItem key={a.value} value={a.value.toString()}>
                    <div>
                      <span>{a.label}</span>
                      {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

      {/* Config input */}
      {needsConfig && action.actionType !== null && (
        <div className="flex-1 w-full sm:w-auto">
          <ActionConfigInput
            actionType={action.actionType}
            configuration={action.configuration}
            onChange={(config) => onUpdate({ configuration: config })}
            organizationId={organizationId}
          />
        </div>
      )}

      {/* Reorder / Remove */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onMove('up')} disabled={index === 0}>
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onMove('down')} disabled={index === total - 1}>
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onRemove}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ── Editor View ──

interface RuleEditorViewProps {
  editingRuleId: string | null;
  organizationId: string;
  onBack: () => void;
}

const RuleEditorView: React.FC<RuleEditorViewProps> = ({ editingRuleId, organizationId, onBack }) => {
  const { data: existingRule } = useAutomationRule(editingRuleId || undefined, organizationId);
  const createMutation = useCreateAutomationRule(organizationId);
  const updateMutation = useUpdateAutomationRule(organizationId);

  const [form, setForm] = useState<EditorFormState>(() => {
    if (!editingRuleId) return { ...INITIAL_FORM, actions: [createEmptyAction()] };
    return INITIAL_FORM; // will be populated by effect
  });
  const [initialized, setInitialized] = useState(!editingRuleId);
  const [errors, setErrors] = useState<string[]>([]);

  // Populate form when editing existing rule
  React.useEffect(() => {
    if (!existingRule || initialized) return;
    setForm({
      name: existingRule.name,
      description: existingRule.description || '',
      ruleType: existingRule.ruleType,
      triggerEvent: existingRule.triggerEvent,
      stopProcessing: existingRule.stopProcessing,
      repeatBehavior: existingRule.repeatBehavior ?? RepeatBehavior.RunOncePerTicket,
      conditionGroups: existingRule.conditionGroups.map((g) => ({
        tempId: crypto.randomUUID(),
        conditions: g.conditions.map((c) => ({
          tempId: crypto.randomUUID(),
          field: c.field,
          operator: c.operator,
          value: c.value || '',
        })),
      })),
      actions: existingRule.actions.length > 0
        ? existingRule.actions.map((a) => ({
            tempId: crypto.randomUUID(),
            actionType: a.actionType,
            configuration: a.configuration || '',
          }))
        : [createEmptyAction()],
    });
    setInitialized(true);
  }, [existingRule, initialized]);

  // Form update helpers
  const updateField = <K extends keyof EditorFormState>(key: K, value: EditorFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors([]);
  };

  const updateCondition = (groupIdx: number, condIdx: number, updates: Partial<EditorCondition>) => {
    setForm((prev) => {
      const groups = [...prev.conditionGroups];
      const conditions = [...groups[groupIdx].conditions];
      conditions[condIdx] = { ...conditions[condIdx], ...updates };
      groups[groupIdx] = { ...groups[groupIdx], conditions };
      return { ...prev, conditionGroups: groups };
    });
  };

  const removeCondition = (groupIdx: number, condIdx: number) => {
    setForm((prev) => {
      const groups = [...prev.conditionGroups];
      const conditions = groups[groupIdx].conditions.filter((_, i) => i !== condIdx);
      if (conditions.length === 0) {
        // Remove the whole group if empty
        return { ...prev, conditionGroups: groups.filter((_, i) => i !== groupIdx) };
      }
      groups[groupIdx] = { ...groups[groupIdx], conditions };
      return { ...prev, conditionGroups: groups };
    });
  };

  const addConditionToGroup = (groupIdx: number) => {
    setForm((prev) => {
      const groups = [...prev.conditionGroups];
      groups[groupIdx] = {
        ...groups[groupIdx],
        conditions: [...groups[groupIdx].conditions, createEmptyCondition()],
      };
      return { ...prev, conditionGroups: groups };
    });
  };

  const addConditionGroup = () => {
    setForm((prev) => ({
      ...prev,
      conditionGroups: [...prev.conditionGroups, createEmptyGroup()],
    }));
  };

  const removeConditionGroup = (groupIdx: number) => {
    setForm((prev) => ({
      ...prev,
      conditionGroups: prev.conditionGroups.filter((_, i) => i !== groupIdx),
    }));
  };

  const updateAction = (idx: number, updates: Partial<EditorAction>) => {
    setForm((prev) => {
      const actions = [...prev.actions];
      actions[idx] = { ...actions[idx], ...updates };
      return { ...prev, actions };
    });
  };

  const removeAction = (idx: number) => {
    setForm((prev) => ({ ...prev, actions: prev.actions.filter((_, i) => i !== idx) }));
  };

  const moveAction = (idx: number, direction: 'up' | 'down') => {
    setForm((prev) => {
      const actions = [...prev.actions];
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= actions.length) return prev;
      [actions[idx], actions[targetIdx]] = [actions[targetIdx], actions[idx]];
      return { ...prev, actions };
    });
  };

  // Validation
  const validate = (): boolean => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push('Rule name is required.');
    if (form.ruleType === AutomationRuleType.Trigger && !form.triggerEvent) {
      errs.push('Trigger event is required for trigger rules.');
    }
    const validActions = form.actions.filter((a) => a.actionType !== null);
    if (validActions.length === 0) errs.push('At least one action is required.');
    // Check incomplete conditions
    let conditionError = false;
    let conditionValueError = false;
    for (const group of form.conditionGroups) {
      for (const cond of group.conditions) {
        if (cond.field !== null && cond.operator === null) {
          conditionError = true;
        }
        if (cond.field !== null && cond.operator !== null
          && cond.operator !== ConditionOperator.IsSet && cond.operator !== ConditionOperator.IsNotSet
          && !cond.value.trim()) {
          conditionValueError = true;
        }
      }
    }
    if (conditionError) errs.push('All conditions with a field must have an operator selected.');
    if (conditionValueError) errs.push('Conditions must have a value (except IsSet/IsNotSet operators).');

    // Check actions requiring configuration
    for (const action of validActions) {
      if (action.actionType !== null && doesActionRequireConfig(action.actionType) && !action.configuration.trim()) {
        errs.push('Some actions require configuration (e.g., agent, status, priority, or category selection).');
        break;
      }
    }

    // Warn: RunOncePerTicket + ReminderCount condition is incompatible
    if (form.ruleType === AutomationRuleType.Automation && form.repeatBehavior === RepeatBehavior.RunOncePerTicket) {
      const usesReminderCount = form.conditionGroups.some((g) =>
        g.conditions.some((c) => c.field === ConditionField.ReminderCountSinceLastActivity)
      );
      if (usesReminderCount) {
        errs.push('"Run once per ticket" prevents the rule from firing multiple times, which makes the Reminder Count condition ineffective. Switch to "Run every time" to allow multiple reminders.');
      }
    }

    setErrors(errs);
    return errs.length === 0;
  };

  // Build DTO from form
  const buildConditionGroups = (): ConditionGroupDto[] => {
    return form.conditionGroups
      .filter((g) => g.conditions.some((c) => c.field !== null))
      .map((g, gi) => ({
        sortOrder: gi,
        conditions: g.conditions
          .filter((c) => c.field !== null && c.operator !== null)
          .map((c, ci) => ({
            field: c.field!,
            operator: c.operator!,
            value: c.value || null,
            sortOrder: ci,
          } as ConditionDto)),
      }));
  };

  const buildActions = (): ActionDto[] => {
    return form.actions
      .filter((a) => a.actionType !== null)
      .map((a, i) => ({
        actionType: a.actionType!,
        configuration: a.configuration || null,
        sortOrder: i,
      }));
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      if (editingRuleId) {
        await updateMutation.mutateAsync({
          id: editingRuleId,
          request: {
            name: form.name.trim(),
            description: form.description.trim(),
            triggerEvent: form.triggerEvent,
            stopProcessing: form.stopProcessing,
            repeatBehavior: form.ruleType === AutomationRuleType.Automation ? form.repeatBehavior : undefined,
            conditionGroups: buildConditionGroups(),
            actions: buildActions(),
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: form.name.trim(),
          description: form.description.trim() || null,
          ruleType: form.ruleType,
          triggerEvent: form.triggerEvent,
          stopProcessing: form.stopProcessing,
          repeatBehavior: form.ruleType === AutomationRuleType.Automation ? form.repeatBehavior : undefined,
          conditionGroups: buildConditionGroups(),
          actions: buildActions(),
        });
      }
      onBack();
    } catch {
      // The create/update mutation's onError already toasts the server's reason; stay in the editor
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (editingRuleId && !initialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-base sm:text-lg font-semibold">
            {editingRuleId ? 'Edit Rule' : 'Create Rule'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {editingRuleId ? 'Modify the automation rule settings.' : 'Define a new automation rule.'}
          </p>
        </div>
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          {errors.map((err, i) => (
            <p key={i} className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {err}
            </p>
          ))}
        </div>
      )}

      {/* Basic Info */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
          <CardTitle className="text-sm font-medium">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
          <div className="space-y-2">
            <Label>Name <span className="text-red-500">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Rule name"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            {!editingRuleId && (
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select
                  value={form.ruleType.toString()}
                  onValueChange={(v) => {
                    const rt = Number(v) as AutomationRuleType;
                    updateField('ruleType', rt);
                    if (rt === AutomationRuleType.Automation) {
                      updateField('triggerEvent', null);
                    }
                  }}
                >
                  <SelectTrigger className="w-full sm:w-72"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AutomationRuleTypeLabels).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        <div>
                          <span>{label}</span>
                          <p className="text-xs text-muted-foreground">
                            {AutomationRuleTypeDescriptions[Number(val) as AutomationRuleType]}
                          </p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.ruleType === AutomationRuleType.Trigger && (
              <div className="space-y-2">
                <Label>Trigger Event <span className="text-red-500">*</span></Label>
                <Select
                  value={form.triggerEvent?.toString() || ''}
                  onValueChange={(v) => {
                    const newEvent = Number(v) as AutomationEvent;
                    const oldEvent = form.triggerEvent;
                    const categoryChanged = oldEvent != null && isArticleEvent(oldEvent) !== isArticleEvent(newEvent);
                    const hasConditions = form.conditionGroups.some(g => g.conditions.some(c => c.field !== null));
                    const hasActions = form.actions.some(a => a.actionType !== null);

                    if (categoryChanged && (hasConditions || hasActions)) {
                      setForm(prev => ({
                        ...prev,
                        triggerEvent: newEvent,
                        conditionGroups: [],
                        actions: [createEmptyAction()],
                      }));
                      setErrors([]);
                      showToast.info('Event Type Changed', 'Conditions and actions have been reset for the new event type');
                    } else {
                      // Same category or no configured items — just update the event
                      // Clear FeedbackIsHelpful conditions when leaving ArticleFeedbackSubmitted
                      if (oldEvent === AutomationEvent.ArticleFeedbackSubmitted && newEvent !== AutomationEvent.ArticleFeedbackSubmitted) {
                        setForm(prev => ({
                          ...prev,
                          triggerEvent: newEvent,
                          conditionGroups: prev.conditionGroups.map(g => ({
                            ...g,
                            conditions: g.conditions.map(c =>
                              c.field === ConditionField.FeedbackIsHelpful
                                ? { ...c, field: null, operator: null, value: '' }
                                : c
                            ),
                          })),
                        }));
                        setErrors([]);
                      } else {
                        updateField('triggerEvent', newEvent);
                      }
                    }
                  }}
                >
                  <SelectTrigger className="w-full sm:w-72"><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AutomationEventLabels).map(([val, label]) => (
                      <SelectItem key={val} value={val}>
                        <div>
                          <span>{label}</span>
                          <p className="text-xs text-muted-foreground">
                            {AutomationEventDescriptions[Number(val) as AutomationEvent]}
                          </p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {form.ruleType === AutomationRuleType.Trigger && (
            <div className="flex items-center gap-3">
              <Switch
                checked={form.stopProcessing}
                onCheckedChange={(checked) => updateField('stopProcessing', checked)}
              />
              <Label className="cursor-pointer" onClick={() => updateField('stopProcessing', !form.stopProcessing)}>
                Stop processing subsequent rules
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-sm">
                    <p>When enabled, no further trigger rules will be evaluated after this rule matches. Rules are evaluated in sort order.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {form.ruleType === AutomationRuleType.Automation && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Repeat Behavior</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-sm">
                      <p>Controls whether this rule re-fires for the same ticket on subsequent background sweeps.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select
                value={form.repeatBehavior.toString()}
                onValueChange={(v) => updateField('repeatBehavior', Number(v) as RepeatBehavior)}
              >
                <SelectTrigger className="w-full sm:w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RepeatBehaviorLabels).map(([val, label]) => (
                    <SelectItem key={val} value={val}>
                      <div>
                        <span>{label}</span>
                        <p className="text-xs text-muted-foreground">
                          {RepeatBehaviorDescriptions[Number(val) as RepeatBehavior]}
                        </p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conditions */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
          <CardTitle className="text-sm font-medium">Conditions (Optional)</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
          {form.conditionGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No conditions — this rule will match all events of the selected type.
            </p>
          ) : (
            form.conditionGroups.map((group, gi) => (
              <div key={group.tempId}>
                {gi > 0 && (
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 border-t" />
                    <Badge variant="secondary" className="text-xs">OR</Badge>
                    <div className="flex-1 border-t" />
                  </div>
                )}
                <div className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-muted-foreground">
                      Condition Group {gi + 1}
                    </span>
                    {form.conditionGroups.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => removeConditionGroup(gi)}>
                        <X className="h-3 w-3 mr-1" /> Remove Group
                      </Button>
                    )}
                  </div>
                  {group.conditions.map((cond, ci) => (
                    <div key={cond.tempId}>
                      {ci > 0 && (
                        <div className="pl-2 py-1">
                          <span className="text-xs font-medium text-muted-foreground">AND</span>
                        </div>
                      )}
                      <ConditionRow
                        condition={cond}
                        onUpdate={(updates) => updateCondition(gi, ci, updates)}
                        onRemove={() => removeCondition(gi, ci)}
                        organizationId={organizationId}
                        ruleType={form.ruleType}
                        triggerEvent={form.triggerEvent}
                        entityContextFields={!form.triggerEvent ? getValidConditionFieldsForEntityContext(
                          getEntityContextFromConditions(form.conditionGroups.flatMap(g => g.conditions.map(c => c.field)))
                        ) : null}
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => addConditionToGroup(gi)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Condition
                  </Button>
                </div>
              </div>
            ))
          )}
          <Button variant="outline" size="sm" onClick={addConditionGroup}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Condition Group
          </Button>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
          <CardTitle className="text-sm font-medium">Actions <span className="text-red-500">*</span></CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-3">
          {(() => {
            // For Automation rules (no triggerEvent), derive entity context from conditions
            const conditionFields = form.conditionGroups.flatMap(g => g.conditions.map(c => c.field));
            const entityContext = !form.triggerEvent ? getEntityContextFromConditions(conditionFields) : null;
            const contextActions = entityContext ? getValidActionTypesForEntityContext(entityContext) : null;

            return form.actions.map((action, i) => (
              <ActionRow
                key={action.tempId}
                action={action}
                index={i}
                total={form.actions.length}
                onUpdate={(updates) => updateAction(i, updates)}
                onRemove={() => removeAction(i)}
                onMove={(dir) => moveAction(i, dir)}
                organizationId={organizationId}
                ruleType={form.ruleType}
                triggerEvent={form.triggerEvent}
                entityContextActions={contextActions}
              />
            ));
          })()}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setForm((prev) => ({ ...prev, actions: [...prev.actions, createEmptyAction()] }))}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Action
          </Button>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {editingRuleId ? 'Update Rule' : 'Create Rule'}
        </Button>
        <Button variant="outline" onClick={onBack} disabled={isSaving}>Cancel</Button>
      </div>
    </div>
  );
};

// ── List View ──

interface RuleListViewProps {
  organizationId: string;
  onEdit: (id: string) => void;
  onCreate: () => void;
}

const RuleListView: React.FC<RuleListViewProps> = ({ organizationId, onEdit, onCreate }) => {
  const { data, isLoading } = useAutomationRules({ pageSize: 100 }, organizationId);
  const toggleMutation = useToggleAutomationRule(organizationId);
  const deleteMutation = useDeleteAutomationRule(organizationId);
  const reorderMutation = useReorderAutomationRules(organizationId);
  const seedMutation = useSeedDefaultAutomationRules(organizationId);

  const triggerRules = useMemo(() => {
    if (!data?.rules) return [];
    return [...data.rules]
      .filter((r) => r.ruleType === AutomationRuleType.Trigger)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [data]);

  const automationRules = useMemo(() => {
    if (!data?.rules) return [];
    return [...data.rules]
      .filter((r) => r.ruleType === AutomationRuleType.Automation)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [data]);

  const handleMove = (rules: AutomationRuleListItem[], index: number, direction: 'up' | 'down') => {
    if (rules.length < 2) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= rules.length) return;

    const reordered = [...rules];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    const items = reordered.map((r, i) => ({ ruleId: r.id, sortOrder: i + 1 }));
    reorderMutation.mutate(items);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasNoRules = triggerRules.length === 0 && automationRules.length === 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold">Automation Rules</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Create rules to automate ticket workflows, notifications, and assignments.
          </p>
        </div>
        <Button onClick={onCreate} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" /> Add Rule
        </Button>
      </div>

      {hasNoRules ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-6 sm:py-8 text-muted-foreground">
              <Zap className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 opacity-50" />
              <p className="text-sm sm:text-base">No automation rules found</p>
              <p className="text-xs sm:text-sm mb-4">Create your first rule to automate workflows.</p>
              <div className="flex items-center gap-2 justify-center">
                <Button onClick={onCreate} variant="outline">
                  <Plus className="mr-2 h-4 w-4" /> Add Rule
                </Button>
                <Button
                  variant="outline"
                  onClick={() => seedMutation.mutate()}
                  disabled={seedMutation.isPending}
                >
                  {seedMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Seed Defaults
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Trigger Rules Section */}
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">Trigger Rules</h4>
              <p className="text-xs text-muted-foreground">
                {AutomationRuleTypeDescriptions[AutomationRuleType.Trigger]}. Evaluated in order — use arrows to reorder.
              </p>
            </div>
            {triggerRules.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">No trigger rules yet.</p>
            ) : (
              triggerRules.map((rule, index) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onEdit={() => onEdit(rule.id)}
                  onToggle={(enabled) => toggleMutation.mutate({ id: rule.id, isEnabled: enabled })}
                  onDelete={() => deleteMutation.mutate(rule.id)}
                  onMoveUp={() => handleMove(triggerRules, index, 'up')}
                  onMoveDown={() => handleMove(triggerRules, index, 'down')}
                  isFirst={index === 0}
                  isLast={index === triggerRules.length - 1}
                  isToggling={toggleMutation.isPending}
                  isDeleting={deleteMutation.isPending}
                  isReordering={reorderMutation.isPending}
                />
              ))
            )}
          </div>

          {/* Scheduled Rules Section */}
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">Scheduled Rules</h4>
              <p className="text-xs text-muted-foreground">
                {AutomationRuleTypeDescriptions[AutomationRuleType.Automation]}. All matching rules run independently.
              </p>
            </div>
            {automationRules.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">No scheduled rules yet.</p>
            ) : (
              automationRules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onEdit={() => onEdit(rule.id)}
                  onToggle={(enabled) => toggleMutation.mutate({ id: rule.id, isEnabled: enabled })}
                  onDelete={() => deleteMutation.mutate(rule.id)}
                  isToggling={toggleMutation.isPending}
                  isDeleting={deleteMutation.isPending}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Rule Card ──

interface RuleCardProps {
  rule: AutomationRuleListItem;
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  isToggling: boolean;
  isDeleting: boolean;
  isReordering?: boolean;
}

const RuleCard: React.FC<RuleCardProps> = ({ rule, onEdit, onToggle, onDelete, onMoveUp, onMoveDown, isFirst, isLast, isToggling, isDeleting, isReordering }) => {
  return (
    <Card className={!rule.isEnabled ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-sm">{rule.name}</span>
              {rule.isDefault && <Badge variant="secondary" className="text-xs">Default</Badge>}
            </div>
            {rule.description && (
              <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{rule.description}</p>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              {rule.triggerEvent && (
                <Badge variant="outline" className="text-xs">
                  {AutomationEventLabels[rule.triggerEvent]}
                </Badge>
              )}
              {rule.ruleType === AutomationRuleType.Automation && (
                <Badge variant="outline" className="text-xs">
                  {RepeatBehaviorLabels[rule.repeatBehavior]}
                </Badge>
              )}
              {rule.conditionGroupCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {rule.conditionGroupCount} condition{rule.conditionGroupCount !== 1 ? 's' : ''}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                {rule.actionCount} action{rule.actionCount !== 1 ? 's' : ''}
              </Badge>
              {rule.stopProcessing && rule.ruleType === AutomationRuleType.Trigger && (
                <Badge variant="destructive" className="text-xs">Stops</Badge>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {onMoveUp && onMoveDown && (
              <div className="flex flex-col">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp} disabled={isFirst || isReordering}>
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown} disabled={isLast || isReordering}>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <Switch
              checked={rule.isEnabled}
              onCheckedChange={(checked) => onToggle(checked)}
              disabled={isToggling}
            />
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            {/* Turning a rule off is how it's retired; only a rule that's off can be deleted */}
            {!rule.isDefault && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={isDeleting || rule.isEnabled}
                    title={rule.isEnabled ? 'Turn the rule off to delete it' : 'Delete rule'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Rule</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{rule.name}"? Its conditions, actions and execution log are
                      deleted. Ticket history keeps showing the changes it made, under its name. This can't be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main Component ──

interface AutomationSettingsProps {
  organizationId?: string;
}

export const AutomationSettings: React.FC<AutomationSettingsProps> = ({ organizationId }) => {
  const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  if (!organizationId) return null;

  if (viewMode === 'editor') {
    return (
      <RuleEditorView
        editingRuleId={editingRuleId}
        organizationId={organizationId}
        onBack={() => {
          setViewMode('list');
          setEditingRuleId(null);
        }}
      />
    );
  }

  return (
    <RuleListView
      organizationId={organizationId}
      onEdit={(id) => {
        setEditingRuleId(id);
        setViewMode('editor');
      }}
      onCreate={() => {
        setEditingRuleId(null);
        setViewMode('editor');
      }}
    />
  );
};
